import type {ModalWindowType} from 'src/modals/modal-conversation.service';
import type {IDocumentService, IHttpService, IOnInit} from 'angular';
import type {Fond, PortaroVersion, User} from 'typings/portaro.be.types';
import type {LocalizationService} from 'shared/providers/localization.provider';
import type {TransitionService} from '@uirouter/angularjs';
import type {Subkind} from 'shared/constants/portaro.constants';
import type {DefinedActionPerformer} from '../defined-actions/defined-action-performer';
import type ModalConversationInterceptorService from '../../modals/modal-conversation-interceptor.service';
import type ModalConversationService from '../../modals/modal-conversation.service';
import type RecordService from '../../features/record/record.service';
import type LoginService from '../login/login.service';
import type ActionRequestService from '../services/action-request.service';
import type FinishedResponseInteractionService from '../services/finished-response-interaction.service';
import type UserService from '../../features/user/user.service';
import type ReloaderService from 'shared/services/reloader.service';
import {getUrlParams} from '../utils/url-utils';
import {isInterruptedActionResponse} from 'src/modals/modal-utils';
import {AuthInterceptorRegistrar} from '../login/auth.interceptor-registrar';
import {UserInterceptorRegistrar} from 'src/features/user/user.interceptor-registrar';
import {SettingInterceptorRegistrar} from 'src/features/settings/setting.interceptor-registrar';
import {LoanInterceptorRegistrar} from 'src/features/loan/loan.interceptor-registrar';
import {PaymentInterceptorRegistrar} from 'src/features/payment/payment.interceptor-registrar';
import KpMainMenu from 'shared/components/kp-main-menu/KpMainMenu.svelte';
import KpHeader from 'shared/components/kp-header/KpHeader.svelte';
import KpFooter from 'shared/components/kp-footer/KpFooter.svelte';
import KpBarcodeScannerViewer from 'shared/barcode-scanner/kp-barcode-scanner-viewer/KpBarcodeScannerViewer.svelte';
import KpBrowserProgressBar from 'shared/components/kp-browser-progress-bar/KpBrowserProgressBar.svelte';
import KpToastMessages from 'shared/components/kp-toast-messages/KpToastMessages.svelte';
import ErpSidebarNav from '../../features/erp/components/erp-sidebar-nav/ErpSidebarNav.svelte';
import ErpTopbar from '../../features/erp/components/erp-topbar/ErpTopbar.svelte';
import {DiscountInterceptorRegistrar} from 'src/features/discount/lib/discount.interceptor-registrar';

export class PageController implements IOnInit {
    public static controllerName = 'PageCtrl';

    public autofocusGlobalSearch = false;
    public globalSearchInputEnabled: boolean;

    public kpMainMenuComponent = KpMainMenu;
    public kpBarcodeScannerViewerComponent = KpBarcodeScannerViewer;
    public kpHeaderComponent = KpHeader;
    public kpFooterComponent = KpFooter;
    public kpToastMessagesComponent = KpToastMessages;
    public kpBrowserProgressBarComponent = KpBrowserProgressBar;

    public erpSidebarNavComponent = ErpSidebarNav;
    public erpTopbarComponent = ErpTopbar;

    /*@ngInject*/
    constructor(private CONTEXT_PATH: string,
                private portaroVersion: PortaroVersion,
                private loginService: LoginService,
                private actionRequestService: ActionRequestService,
                private modalConversationService: ModalConversationService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private $document: IDocumentService,
                localizationService: LocalizationService,
                definedActionPerformer: DefinedActionPerformer,
                modalConversationInterceptor: ModalConversationInterceptorService,
                recordService: RecordService,
                userService: UserService,
                reloader: ReloaderService,
                $http: IHttpService,
                $transitions: TransitionService,
                GLOBAL_SEARCH_INPUT_ENABLED: boolean
    ) {
        definedActionPerformer.registerMappedAction('record-holding-creation', (action) => recordService.createRecordHoldingAndAddExemplarsThrowingToModal(action.params?.subkind as Subkind, action.params?.fond as Fond));
        definedActionPerformer.registerMappedAction('record-holding-creation-no-record-presearch', (action) => recordService.createRecordHoldingAndAddExemplarsThrowingToModal(action.params?.subkind as Subkind, action.params?.fond as Fond, false));
        definedActionPerformer.registerMappedAction('extend-user-registration', async (action) => {
            await userService.extendRegistration({id: action.params?.id} as User);
            return reloader.reloadRoute();
        });

        new AuthInterceptorRegistrar(modalConversationInterceptor, loginService, $http).register();
        new UserInterceptorRegistrar(modalConversationInterceptor).register();
        new SettingInterceptorRegistrar(modalConversationInterceptor).register();
        new PaymentInterceptorRegistrar(modalConversationInterceptor).register();
        new DiscountInterceptorRegistrar(modalConversationInterceptor).register();
        new LoanInterceptorRegistrar(modalConversationInterceptor, localizationService).register();

        $transitions.onEnter({to: 'home'}, () => this.autofocusGlobalSearch = true);
        this.globalSearchInputEnabled = GLOBAL_SEARCH_INPUT_ENABLED;

        this.openDialogsInChain();
    }

    public skipToContent() {
        this.$document[0].querySelector('main')?.focus();
    }

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    public async $onInit() {
        await this.showLoginIfInParam();
        await this.doActionRequestIfInParam();
    }

    private async showLoginIfInParam() {
        const urlParams = getUrlParams();
        if (urlParams.showLogin) {
            const redirect = urlParams.redirect;
            if (redirect) {
                await this.loginService.goLogged(redirect);
            } else {
                await this.loginService.thisPageLogged();
            }
        }
    }

    private async doActionRequestIfInParam() {
        const urlParams = getUrlParams();
        if (urlParams.actionRequest) {
            const actionRequest = JSON.parse(urlParams.actionRequest);
            await this.actionRequestService.action(actionRequest.path, actionRequest.method, actionRequest.body);
        }
    }

    private async openDialogsInChain() {
        for (const dialog of window.dialogs) {
            try {
                const actionResponse = await this.modalConversationService.openModalWindowAndGetFinishedResponse(dialog.responseType as ModalWindowType, dialog, this.isComponent(dialog.responseType) ? {isComponent: true} : null);
                await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
            } catch (exceptionResponse) {
                if (!isInterruptedActionResponse(exceptionResponse)) {
                    this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
                }
            }
        }
    }

    private isComponent(responseType: string): boolean {
        return /^[a-z][a-zA-Z0-9]+$/.test(responseType); // lower camelCase test
    }
}
